import { createPublicClient, createWalletClient, custom, erc20Abi, getContract, http, parseEther } from "viem";
import { privateKeyToAccount } from "viem/accounts";
import { base } from "viem/chains";
import { BondkitTokenABI } from "./abis/index.js";
import { getConfig } from "./config.js";
import { BondkitSwapService } from "./swapService.js";
import { TokenStatus } from "./types.js";
// Event ABI snippets for decoding
const boughtEventAbi = BondkitTokenABI.find(item => item.type === "event" && item.name === "BondingCurveBuy");
const soldEventAbi = BondkitTokenABI.find(item => item.type === "event" && item.name === "BondingCurveSell");
const dexMigrationEventAbi = BondkitTokenABI.find(item => item.type === "event" && item.name === "BondkitTokenMigrated");
// OKX wallet polling constants
const OKX_POLLING_MAX_RETRIES = 60; // 5 minutes with 5 second intervals
const OKX_POLLING_INTERVAL_MS = 5000; // 5 seconds
export class BondkitToken {
    constructor(contractAddress, walletKey, rpcUrl) {
        const sdkConfig = getConfig(base.id, rpcUrl);
        this.chain = sdkConfig.chain;
        this.rpcUrl = sdkConfig.rpcUrl;
        this.apiEndpoint = sdkConfig.apiEndpoint;
        this.chartApiEndpoint = sdkConfig.chartApiEndpoint;
        if (walletKey && !walletKey.startsWith("0x")) {
            this.walletKey = `0x${walletKey}`;
        }
        else if (walletKey) {
            this.walletKey = walletKey;
        }
        if (!contractAddress || !contractAddress.startsWith("0x")) {
            throw new Error("Valid contract address is required for BondkitToken.");
        }
        this.contractAddress = contractAddress;
        this.publicClient = createPublicClient({
            chain: this.chain,
            transport: http(this.rpcUrl),
        });
        this.walletClientInstance = createWalletClient({
            chain: this.chain,
            transport: http(this.rpcUrl),
            account: this.walletKey ? privateKeyToAccount(this.walletKey) : undefined,
        });
        this.contract = getContract({
            address: this.contractAddress,
            abi: BondkitTokenABI,
            client: {
                public: this.publicClient,
                wallet: this.walletClientInstance,
            },
        });
        getContract({
            address: this.contractAddress,
            abi: BondkitTokenABI,
            client: this.publicClient, // Use public client for read operations
        })
            .read.tradingToken()
            .then(tradingToken => {
            this.tradingToken = tradingToken;
        });
    }
    connect(provider) {
        try {
            const transport = provider ? custom(provider) : http(this.rpcUrl);
            this.connectedProvider = provider;
            this.walletClientInstance = createWalletClient({
                chain: this.chain,
                transport,
                account: this.walletKey ? privateKeyToAccount(this.walletKey) : undefined,
            });
            this.publicClient = createPublicClient({
                chain: this.chain,
                transport: http(this.rpcUrl),
            });
            this.contract = getContract({
                address: this.contractAddress,
                abi: BondkitTokenABI,
                client: {
                    public: this.publicClient,
                    wallet: this.walletClientInstance,
                },
            });
            return true;
        }
        catch (error) {
            console.error("Connection failed:", error);
            return false;
        }
    }
    /**
     * Connects using an EIP-1193 provider and requests accounts, selecting the first one.
     * Enables frontend wallet flows without requiring a private key.
     */
    async connectWithProvider(provider) {
        try {
            const transport = custom(provider);
            this.connectedProvider = provider;
            // Try to request accounts (prompt user if needed)
            let addresses = [];
            try {
                const result = await provider.request({ method: "eth_requestAccounts" });
                if (Array.isArray(result))
                    addresses = result;
            }
            catch (requestErr) {
                try {
                    const result = await provider.request({ method: "eth_accounts" });
                    if (Array.isArray(result))
                        addresses = result;
                }
                catch (_) {
                    // ignore
                }
            }
            const selectedAccount = (addresses?.[0] ?? undefined);
            this.walletClientInstance = createWalletClient({
                chain: this.chain,
                transport,
                account: selectedAccount,
            });
            this.publicClient = createPublicClient({
                chain: this.chain,
                transport: http(this.rpcUrl),
            });
            this.contract = getContract({
                address: this.contractAddress,
                abi: BondkitTokenABI,
                client: {
                    public: this.publicClient,
                    wallet: this.walletClientInstance,
                },
            });
            return true;
        }
        catch (error) {
            console.error("Connection failed:", error);
            return false;
        }
    }
    async handleError(error, context) {
        const defaultMessage = context ? `Error in ${context}:` : "An error occurred:";
        console.error(defaultMessage, error);
        // TODO: Add more specific error checks based on BondkitTokenABI error types if needed
        throw error;
    }
    // --- Read Methods (ERC20 + Custom) --- //
    async name() {
        try {
            return await this.contract.read.name();
        }
        catch (error) {
            console.warn("Error fetching token name:", error);
            return undefined;
        }
    }
    async symbol() {
        try {
            return await this.contract.read.symbol();
        }
        catch (error) {
            console.warn("Error fetching token symbol:", error);
            return undefined;
        }
    }
    async decimals() {
        try {
            const dec = await this.contract.read.decimals();
            return Number(dec);
        }
        catch (error) {
            console.warn("Error fetching token decimals:", error);
            return undefined;
        }
    }
    async totalSupply() {
        try {
            return await this.contract.read.totalSupply();
        }
        catch (error) {
            console.warn("Error fetching token total supply:", error);
            return undefined;
        }
    }
    async balanceOf(account) {
        try {
            return await this.contract.read.balanceOf([account]);
        }
        catch (error) {
            console.warn(`Error fetching balance for ${account}:`, error);
            return undefined;
        }
    }
    async getTradingTokenBalanceOf(account) {
        try {
            if (!this.tradingToken) {
                console.warn("Trading token address not available");
                return undefined;
            }
            // If trading token is ETH (zero address), get ETH balance
            if (this.tradingToken === "0x0000000000000000000000000000000000000000") {
                return await this.publicClient.getBalance({ address: account });
            }
            // For ERC20 trading tokens, get token balance
            const tradingTokenContract = getContract({
                address: this.tradingToken,
                abi: erc20Abi,
                client: this.publicClient, // Use public client for read operations
            });
            return await tradingTokenContract.read.balanceOf([account]);
        }
        catch (error) {
            console.warn(`Error fetching trading token balance for ${account}:`, error);
            return undefined;
        }
    }
    async getTradingTokenAddress() {
        try {
            if (!this.tradingToken) {
                this.tradingToken = (await this.contract.read.tradingToken());
            }
            return this.tradingToken;
        }
        catch (error) {
            console.warn("Error fetching trading token address:", error);
            return undefined;
        }
    }
    async allowance(owner, spender) {
        try {
            return await this.contract.read.allowance([owner, spender]);
        }
        catch (error) {
            console.warn(`Error fetching allowance for owner ${owner} and spender ${spender}:`, error);
            return undefined;
        }
    }
    async owner() {
        try {
            return await this.contract.read.owner();
        }
        catch (error) {
            console.warn("Error fetching token owner or 'owner' function might not exist:", error);
            return undefined;
        }
    }
    async getTokenDetails() {
        try {
            const [name, symbol, decimals, totalSupply, owner] = await Promise.all([
                this.name(),
                this.symbol(),
                this.decimals(),
                this.totalSupply(),
                this.owner(),
            ]);
            if (name === undefined || symbol === undefined || decimals === undefined || totalSupply === undefined) {
                console.warn("Failed to retrieve all essential token details.");
                return undefined;
            }
            return {
                name,
                symbol,
                decimals,
                totalSupply,
                owner: owner || "0x0000000000000000000000000000000000000000",
            };
        }
        catch (error) {
            console.warn("Error in getTokenDetails:", error);
            return undefined;
        }
    }
    // --- Bondkit Specific Read Methods ---
    async feeRecipient() {
        try {
            return await this.contract.read.feeRecipient();
        }
        catch (e) {
            console.warn("Error fetching feeRecipient:", e);
            return undefined;
        }
    }
    async currentStatus() {
        try {
            const status = await this.contract.read.currentStatus();
            return status;
        }
        catch (e) {
            console.warn("Error fetching currentStatus:", e);
            return undefined;
        }
    }
    async getCurrentPhase() {
        try {
            return await this.contract.read.getCurrentPhase();
        }
        catch (e) {
            console.warn("Error fetching getCurrentPhase:", e);
            return undefined;
        }
    }
    async getAmountOfTokensToBuy(ethAmount) {
        try {
            const value = typeof ethAmount === "string" ? parseEther(ethAmount) : ethAmount;
            return await this.contract.read.getAmountOfTokensToBuy([value]);
        }
        catch (e) {
            console.warn("Error in getAmountOfTokensToBuy:", e);
            return undefined;
        }
    }
    async getAmountOfTradingTokensToSell(amount) {
        try {
            return await this.contract.read.getAmountOfTradingTokensToSell([amount]);
        }
        catch (e) {
            console.warn("Error in getAmountOfTradingTokensToSell:", e);
            return undefined;
        }
    }
    async getCurrentBondingCurvePricePerToken() {
        try {
            return await this.contract.read.getCurrentBondingCurvePricePerToken();
        }
        catch (e) {
            console.warn("Error fetching current bonding curve price:", e);
            return undefined;
        }
    }
    async totalRaisedBonding() {
        try {
            return await this.contract.read.totalRaisedBonding();
        }
        catch (e) {
            console.warn("Error fetching totalRaisedBonding:", e);
            return undefined;
        }
    }
    async getTotalSupply() {
        try {
            return await this.contract.read.totalSupply();
        }
        catch (e) {
            console.warn("Error fetching total supply:", e);
            return undefined;
        }
    }
    async getPaginatedHolders(startIndex, count = BigInt(1000)) {
        try {
            const response = await this.contract.read.getPaginatedHolders([startIndex, count]);
            const holders = response[0];
            const balances = response[1];
            return holders.map((holder, index) => ({
                address: holder,
                balance: balances[index],
            }));
        }
        catch (e) {
            console.warn("Error fetching paginated holders:", e);
            return [];
        }
    }
    async getBondingProgress() {
        try {
            const [progress, raised, threshold] = await this.contract.read.getBondingProgressPercent();
            return {
                progress: Number(progress) / 100,
                raised: Number(raised),
                threshold: Number(threshold),
            };
        }
        catch (e) {
            console.warn("Error fetching bonding progress percent:", e);
            return undefined;
        }
    }
    // --- Transaction History --- //
    async getTransactionHistory(options) {
        try {
            const { userAddress, type, from, to, limit, offset } = options || {};
            const response = await fetch(this.apiEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-service-method": "getTransactionHistory",
                },
                body: JSON.stringify({
                    contractAddress: this.contractAddress,
                    chainId: this.chain.id,
                    userAddress,
                    type,
                    from,
                    to: to || Date.now(),
                    limit: limit || 50,
                    offset,
                }),
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const result = await response.json();
            return result;
        }
        catch (e) {
            console.warn("Error fetching transaction history:", e);
            return undefined;
        }
    }
    // --- Market Cap Chart Data --- //
    async getMarketCapChartData() {
        try {
            const url = new URL("/recent_marketcap", this.chartApiEndpoint);
            url.searchParams.set("contractAddress", this.contractAddress);
            url.searchParams.set("chainId", this.chain.id.toString());
            const response = await fetch(url.toString());
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const result = await response.json();
            return result;
        }
        catch (e) {
            console.warn("Error fetching market cap chart data:", e);
            return undefined;
        }
    }
    // --- Write Methods --- //
    async executeWrite(functionName, args, options) {
        if (!this.walletClientInstance.account && !this.walletKey) {
            // Try to resolve an account from a connected EIP-1193 provider on-demand
            if (this.connectedProvider) {
                try {
                    const addresses = (await this.connectedProvider.request({ method: "eth_accounts" }));
                    const selectedAccount = (addresses?.[0] ?? undefined);
                    if (selectedAccount) {
                        const transport = custom(this.connectedProvider);
                        this.walletClientInstance = createWalletClient({
                            chain: this.chain,
                            transport,
                            account: selectedAccount,
                        });
                        this.contract = getContract({
                            address: this.contractAddress,
                            abi: BondkitTokenABI,
                            client: {
                                public: this.publicClient,
                                wallet: this.walletClientInstance,
                            },
                        });
                    }
                }
                catch (_) { }
            }
            if (!this.walletClientInstance.account && !this.walletKey) {
                throw new Error("Wallet key not set or client not connected for write operation.");
            }
        }
        // Only attempt chain switching for browser wallet providers
        // Private key users cannot switch chains programmatically
        if (this.connectedProvider && this.walletClientInstance.account) {
            const walletChainId = await this.walletClientInstance.getChainId();
            if (walletChainId !== this.chain.id) {
                try {
                    await this.walletClientInstance.switchChain({ id: this.chain.id });
                }
                catch (switchErr) {
                    throw new Error(`Please switch your wallet to ${this.chain.name} (${this.chain.id}).`);
                }
            }
        }
        const accountToUse = this.walletKey ? privateKeyToAccount(this.walletKey) : this.walletClientInstance.account;
        if (!accountToUse)
            throw new Error("Account for transaction could not be determined.");
        try {
            let maxFee = options?.maxFeePerGas;
            let priorityFee = options?.maxPriorityFeePerGas;
            if (maxFee === undefined || priorityFee === undefined) {
                try {
                    const feeEstimates = await this.publicClient.estimateFeesPerGas();
                    if (maxFee === undefined) {
                        maxFee = feeEstimates.maxFeePerGas;
                    }
                    if (priorityFee === undefined) {
                        priorityFee = feeEstimates.maxPriorityFeePerGas;
                    }
                }
                catch (feeError) {
                    console.warn("Could not estimate fees, will rely on wallet defaults or provided values:", feeError);
                    maxFee = maxFee ?? undefined;
                    priorityFee = priorityFee ?? undefined;
                }
            }
            const transactionOptions = {
                account: accountToUse,
                chain: this.chain,
            };
            if (options?.value !== undefined)
                transactionOptions.value = options.value;
            if (options?.gas !== undefined)
                transactionOptions.gas = options.gas;
            if (maxFee !== undefined)
                transactionOptions.maxFeePerGas = maxFee;
            if (priorityFee !== undefined)
                transactionOptions.maxPriorityFeePerGas = priorityFee;
            const hash = await this.contract.write[functionName](args, transactionOptions);
            return hash;
        }
        catch (error) {
            return this.handleError(error, functionName);
        }
    }
    /** Helper method to wait for transaction confirmation with OKX wallet fallback */
    async waitForTransaction(hash) {
        const isOKX = (typeof window !== "undefined" && window.ethereum?.isOKXWallet) || window.okxwallet;
        if (isOKX) {
            // Fallback to polling for OKX wallet
            let retries = 0;
            while (retries < OKX_POLLING_MAX_RETRIES) {
                try {
                    const receipt = await this.publicClient.getTransactionReceipt({ hash });
                    if (receipt) {
                        return receipt;
                    }
                }
                catch (error) {
                    if (error.name !== "TransactionReceiptNotFoundError") {
                        throw error;
                    }
                }
                await new Promise(resolve => setTimeout(resolve, OKX_POLLING_INTERVAL_MS));
                retries++;
            }
            throw new Error("Transaction confirmation timeout");
        }
        else {
            // Use normal waitForTransactionReceipt for other wallets
            return await this.publicClient.waitForTransactionReceipt({ hash });
        }
    }
    async initialize(config, options) {
        return this.executeWrite("initialize", [config], options);
    }
    async transfer(to, amount, options) {
        return this.executeWrite("transfer", [to, amount], options);
    }
    async approve(spender, amount, options) {
        return this.executeWrite("approve", [spender, amount], options);
    }
    async transferFrom(from, to, amount, options) {
        return this.executeWrite("transferFrom", [from, to, amount], options);
    }
    /** Buy tokens with ETH. Payable function. */
    async buy(amount, minTokensOut, options) {
        if (!boughtEventAbi)
            console.warn("Bought event ABI not found for event decoding.");
        if (this.tradingToken === "0x0000000000000000000000000000000000000000") {
            const value = typeof amount === "string" ? parseEther(amount) : amount;
            return this.executeWrite("buy", [minTokensOut], { ...options, value });
        }
        else {
            // For ERC20 trading tokens, we need to approve first, then call buy
            const tradingTokenContract = getContract({
                address: this.tradingToken,
                abi: erc20Abi,
                client: {
                    public: this.publicClient,
                    wallet: this.walletClientInstance,
                },
            });
            const currentAllowance = await tradingTokenContract.read.allowance([
                this.walletClientInstance.account?.address,
                this.contractAddress,
            ]);
            const amountBigInt = typeof amount === "string" ? parseEther(amount) : BigInt(amount);
            if (currentAllowance < amountBigInt) {
                // Get account for the approve transaction
                const accountToUse = this.walletKey ? privateKeyToAccount(this.walletKey) : this.walletClientInstance.account;
                if (!accountToUse)
                    throw new Error("Account for transaction could not be determined.");
                // Create approve options with required fields but without value (ERC20 approve doesn't need ETH)
                const approveOptions = {
                    account: accountToUse,
                    chain: this.chain,
                };
                // Add optional transaction parameters if provided
                if (options?.gas !== undefined)
                    approveOptions.gas = options.gas;
                if (options?.maxFeePerGas !== undefined)
                    approveOptions.maxFeePerGas = options.maxFeePerGas;
                if (options?.maxPriorityFeePerGas !== undefined)
                    approveOptions.maxPriorityFeePerGas = options.maxPriorityFeePerGas;
                const approveTx = await tradingTokenContract.write.approve([this.contractAddress, amountBigInt], approveOptions);
                await this.waitForTransaction(approveTx);
            }
            // Now call the buy function with the trading token amount
            return this.executeWrite("buy", [amountBigInt, minTokensOut], options);
        }
    }
    /** Sell tokens for ETH. */
    async sell(tokenAmount, minEthOut, options) {
        if (!soldEventAbi)
            console.warn("Sold event ABI not found for event decoding.");
        return this.executeWrite("sell", [tokenAmount, minEthOut], options);
    }
    /** Migrate liquidity to DEX. Only callable by owner/migrationAdmin based on typical patterns. */
    async migrateToDex(options) {
        if (!dexMigrationEventAbi)
            console.warn("DexMigration event ABI not found for event decoding.");
        return this.executeWrite("migrateToDex", [], options);
    }
    async transferTokenOwnership(newOwner, options) {
        return this.executeWrite("transferOwnership", [newOwner], options);
    }
    async renounceTokenOwnership(options) {
        return this.executeWrite("renounceOwnership", [], options);
    }
    // --- DEX Swap Methods ---
    /**
     * Get the swap service instance (lazy initialization)
     */
    getSwapService() {
        if (!this.swapService) {
            this.swapService = new BondkitSwapService(this.contractAddress);
        }
        return this.swapService;
    }
    /**
     * Check if DEX swapping is available (token must be in Dex phase)
     */
    async isSwapAvailable() {
        try {
            const status = await this.currentStatus();
            return status === TokenStatus.Dex;
        }
        catch (error) {
            console.warn("Error checking swap availability:", error);
            return undefined;
        }
    }
    /**
     * Get swap quote for trading token → bondkit token
     */
    async getSwapQuoteForBondkitToken(amountTradingTokenIn, slippageTolerance = 0.5) {
        try {
            // Check if swapping is available
            const swapAvailable = await this.isSwapAvailable();
            if (!swapAvailable) {
                console.warn("DEX swapping not available - token must be in Dex phase");
                return undefined;
            }
            const tradingTokenAddress = await this.getTradingTokenAddress();
            if (!tradingTokenAddress) {
                console.warn("Trading token address not available");
                return undefined;
            }
            // Get token details for decimals
            const [tradingTokenDecimals, bondkitTokenDecimals] = await Promise.all([
                this.getTradingTokenDecimals(tradingTokenAddress),
                this.decimals(),
            ]);
            if (tradingTokenDecimals === undefined || bondkitTokenDecimals === undefined) {
                console.warn("Unable to fetch token decimals");
                return undefined;
            }
            const swapService = this.getSwapService();
            const quote = await swapService.getSwapQuote({
                tokenIn: tradingTokenAddress,
                tokenOut: this.contractAddress,
                amountIn: amountTradingTokenIn,
                tokenInDecimals: tradingTokenDecimals,
                tokenOutDecimals: bondkitTokenDecimals,
                slippageTolerance,
                recipient: this.walletClientInstance.account?.address || "0x0000000000000000000000000000000000000000",
            });
            return quote || undefined;
        }
        catch (error) {
            console.warn("Error getting swap quote for bondkit token:", error);
            return undefined;
        }
    }
    /**
     * Get swap quote for bondkit token → trading token
     */
    async getSwapQuoteForTradingToken(amountBondkitTokenIn, slippageTolerance = 0.5) {
        try {
            // Check if swapping is available
            const swapAvailable = await this.isSwapAvailable();
            if (!swapAvailable) {
                console.warn("DEX swapping not available - token must be in Dex phase");
                return undefined;
            }
            const tradingTokenAddress = await this.getTradingTokenAddress();
            if (!tradingTokenAddress) {
                console.warn("Trading token address not available");
                return undefined;
            }
            // Get token details for decimals
            const [bondkitTokenDecimals, tradingTokenDecimals] = await Promise.all([
                this.decimals(),
                this.getTradingTokenDecimals(tradingTokenAddress),
            ]);
            if (bondkitTokenDecimals === undefined || tradingTokenDecimals === undefined) {
                console.warn("Unable to fetch token decimals");
                return undefined;
            }
            const swapService = this.getSwapService();
            const quote = await swapService.getSwapQuote({
                tokenIn: this.contractAddress,
                tokenOut: tradingTokenAddress,
                amountIn: amountBondkitTokenIn,
                tokenInDecimals: bondkitTokenDecimals,
                tokenOutDecimals: tradingTokenDecimals,
                slippageTolerance,
                recipient: this.walletClientInstance.account?.address || "0x0000000000000000000000000000000000000000",
            });
            return quote || undefined;
        }
        catch (error) {
            console.warn("Error getting swap quote for trading token:", error);
            return undefined;
        }
    }
    /**
     * Swap trading token for bondkit token
     */
    async swapTradingTokenForBondkitToken(amountTradingTokenIn, slippageTolerance = 0.5, options) {
        try {
            // Check if swapping is available
            const swapAvailable = await this.isSwapAvailable();
            if (!swapAvailable) {
                console.warn("DEX swapping not available - token must be in Dex phase");
                return undefined;
            }
            if (!this.walletClientInstance.account && !this.walletKey) {
                console.warn("Wallet key not set or client not connected for swap operation");
                return undefined;
            }
            const tradingTokenAddress = await this.getTradingTokenAddress();
            if (!tradingTokenAddress) {
                console.warn("Trading token address not available");
                return undefined;
            }
            // Get token details for decimals
            const [tradingTokenDecimals, bondkitTokenDecimals] = await Promise.all([
                this.getTradingTokenDecimals(tradingTokenAddress),
                this.decimals(),
            ]);
            if (tradingTokenDecimals === undefined || bondkitTokenDecimals === undefined) {
                console.warn("Unable to fetch token decimals");
                return undefined;
            }
            const recipient = this.walletClientInstance.account?.address ||
                (this.walletKey ? privateKeyToAccount(this.walletKey).address : undefined);
            if (!recipient) {
                console.warn("Unable to determine recipient address");
                return undefined;
            }
            const swapService = this.getSwapService();
            const txHash = await swapService.executeSwap({
                tokenIn: tradingTokenAddress,
                tokenOut: this.contractAddress,
                amountIn: amountTradingTokenIn,
                tokenInDecimals: tradingTokenDecimals,
                tokenOutDecimals: bondkitTokenDecimals,
                slippageTolerance,
                recipient,
                deadline: (options?.value ? Math.floor(Date.now() / 1000) : 0) + 3600,
            }, this.walletClientInstance);
            return txHash ? txHash : undefined;
        }
        catch (error) {
            console.warn("Error swapping trading token for bondkit token:", error);
            return undefined;
        }
    }
    /**
     * Swap bondkit token for trading token
     */
    async swapBondkitTokenForTradingToken(amountBondkitTokenIn, slippageTolerance = 0.5, options) {
        try {
            // Check if swapping is available
            const swapAvailable = await this.isSwapAvailable();
            if (!swapAvailable) {
                console.warn("DEX swapping not available - token must be in Dex phase");
                return undefined;
            }
            if (!this.walletClientInstance.account && !this.walletKey) {
                console.warn("Wallet key not set or client not connected for swap operation");
                return undefined;
            }
            const tradingTokenAddress = await this.getTradingTokenAddress();
            if (!tradingTokenAddress) {
                console.warn("Trading token address not available");
                return undefined;
            }
            // Get token details for decimals
            const [bondkitTokenDecimals, tradingTokenDecimals] = await Promise.all([
                this.decimals(),
                this.getTradingTokenDecimals(tradingTokenAddress),
            ]);
            if (bondkitTokenDecimals === undefined || tradingTokenDecimals === undefined) {
                console.warn("Unable to fetch token decimals");
                return undefined;
            }
            const recipient = this.walletClientInstance.account?.address ||
                (this.walletKey ? privateKeyToAccount(this.walletKey).address : undefined);
            if (!recipient) {
                console.warn("Unable to determine recipient address");
                return undefined;
            }
            const swapService = this.getSwapService();
            const txHash = await swapService.executeSwap({
                tokenIn: this.contractAddress,
                tokenOut: tradingTokenAddress,
                amountIn: amountBondkitTokenIn,
                tokenInDecimals: bondkitTokenDecimals,
                tokenOutDecimals: tradingTokenDecimals,
                slippageTolerance,
                recipient,
                deadline: (options?.value ? Math.floor(Date.now() / 1000) : 0) + 3600,
            }, this.walletClientInstance);
            return txHash ? txHash : undefined;
        }
        catch (error) {
            console.warn("Error swapping bondkit token for trading token:", error);
            return undefined;
        }
    }
    /**
     * Helper method to get trading token decimals
     */
    async getTradingTokenDecimals(tradingTokenAddress) {
        try {
            // ETH has 18 decimals
            if (tradingTokenAddress === "0x0000000000000000000000000000000000000000") {
                return 18;
            }
            // For ERC20 tokens, read decimals from contract
            const tradingTokenContract = getContract({
                address: tradingTokenAddress,
                abi: erc20Abi,
                client: this.publicClient,
            });
            const decimals = await tradingTokenContract.read.decimals();
            return Number(decimals);
        }
        catch (error) {
            console.warn("Error fetching trading token decimals:", error);
            return undefined;
        }
    }
    /**
     * Get trading token symbol
     * @param tradingTokenAddress Optional trading token address to avoid fetching it again
     */
    async getTradingTokenSymbol(tradingTokenAddress) {
        try {
            const tokenAddress = tradingTokenAddress || (await this.getTradingTokenAddress());
            if (!tokenAddress) {
                return undefined;
            }
            // ETH symbol
            if (tokenAddress === "0x0000000000000000000000000000000000000000") {
                return "ETH";
            }
            // For ERC20 tokens, read symbol from contract
            const tradingTokenContract = getContract({
                address: tokenAddress,
                abi: erc20Abi,
                client: this.publicClient,
            });
            const symbol = await tradingTokenContract.read.symbol();
            return symbol;
        }
        catch (error) {
            console.warn("Error fetching trading token symbol:", error);
            return undefined;
        }
    }
}
